import React from 'react';
import { ChevronRightIcon } from '../icons/ChevronRightIcon';
import { ChevronLeftIcon } from '../icons/ChevronLeftIcon';
import { TrashIcon } from '../icons/TrashIcon';

const FONT_OPTIONS = ['sans-serif', 'serif', 'monospace', 'cursive', 'fantasy'];
const TRANSITION_TYPES = ['none', 'fade-in', 'fade-out', 'cross-dissolve'];

const Field = ({ label, children }) => (
    <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
        {children}
    </div>
);

const inputCls = "w-full p-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-violet-500/50 transition-colors";
const selectCls = "w-full p-2 bg-[#1a1a24] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-violet-500/50 transition-colors [&>option]:bg-[#1a1a24] [&>option]:text-white";

export const InspectorPanel = ({ selectedClip, project, onUpdateClip, onDeleteClip, isCollapsed, setIsCollapsed }) => {
    if (isCollapsed) {
        return (
            <div className="bg-[#0A0A0F] border-l border-white/10 flex flex-col p-2 items-center shrink-0">
                <button onClick={() => setIsCollapsed(false)} className="p-2 rounded-lg hover:bg-white/10 transition-colors" aria-label="Expand">
                    <ChevronLeftIcon className="w-5 h-5 text-zinc-400" />
                </button>
            </div>
        );
    }

    if (!selectedClip) {
        return (
            <div className="w-[280px] bg-[#0A0A0F] border-l border-white/10 flex flex-col p-4 overflow-y-auto shrink-0 relative">
                <button onClick={() => setIsCollapsed(true)} className="absolute top-2 right-2 p-2 rounded-lg hover:bg-white/10 transition-colors z-10" aria-label="Collapse">
                    <ChevronRightIcon className="w-5 h-5 text-zinc-400" />
                </button>
                <h2 className="text-sm font-bold text-white mb-4">Properties</h2>
                <div className="flex-grow flex items-center justify-center text-zinc-600 text-xs">
                    <p>Select a clip to edit</p>
                </div>
            </div>
        );
    }

    const handleUpdate = (updates) => onUpdateClip({ ...selectedClip, ...updates });

    const isOverlay = selectedClip.type === 'text' || selectedClip.type === 'logo' || selectedClip.type === 'image';

    return (
        <div className="w-[280px] bg-[#0A0A0F] border-l border-white/10 flex flex-col p-4 space-y-4 overflow-y-auto shrink-0 relative">
            <button onClick={() => setIsCollapsed(true)} className="absolute top-2 right-2 p-2 rounded-lg hover:bg-white/10 transition-colors z-10" aria-label="Collapse">
                <ChevronRightIcon className="w-5 h-5 text-zinc-400" />
            </button>
            <div>
                <h2 className="text-sm font-bold text-white">Properties</h2>
                <p className="text-[10px] text-zinc-600 capitalize">{selectedClip.type} clip</p>
            </div>

            {/* Timing */}
            <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Timing</p>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Start (s)">
                        <input type="number" step="0.1" value={selectedClip.start.toFixed(2)} onChange={(e) => handleUpdate({ start: parseFloat(e.target.value) || 0 })} className={inputCls} />
                    </Field>
                    <Field label="Duration (s)">
                        <input type="number" step="0.1" value={selectedClip.duration.toFixed(2)} onChange={(e) => handleUpdate({ duration: Math.max(0.1, parseFloat(e.target.value) || 0.1) })} className={inputCls} />
                    </Field>
                </div>
            </div>

            {/* Transform — for overlays (text, logo, image) */}
            {isOverlay && (
                <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Position</p>
                    {/* 3x3 position grid */}
                    <div className="grid grid-cols-3 gap-1 w-24 mx-auto">
                        {[
                            { label: 'TL', x: 4, y: 4 },
                            { label: 'TC', x: 50, y: 4 },
                            { label: 'TR', x: 96, y: 4 },
                            { label: 'ML', x: 4, y: 50 },
                            { label: 'C',  x: 50, y: 50 },
                            { label: 'MR', x: 96, y: 50 },
                            { label: 'BL', x: 4, y: 96 },
                            { label: 'BC', x: 50, y: 96 },
                            { label: 'BR', x: 96, y: 96 },
                        ].map(pos => {
                            const isActive = Math.round(selectedClip.x ?? 50) === pos.x && Math.round(selectedClip.y ?? 50) === pos.y;
                            return (
                                <button
                                    key={pos.label}
                                    type="button"
                                    onClick={() => handleUpdate({ x: pos.x, y: pos.y })}
                                    className={`w-7 h-7 rounded-md text-[9px] font-bold transition-all ${
                                        isActive
                                            ? 'bg-violet-600 text-white'
                                            : 'bg-white/5 border border-white/10 text-zinc-500 hover:bg-white/10 hover:text-zinc-300'
                                    }`}
                                    title={pos.label}
                                >
                                    <div className={`w-1.5 h-1.5 rounded-full mx-auto ${isActive ? 'bg-white' : 'bg-zinc-500'}`} />
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 pt-1">Fine Tune</p>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="X (%)">
                            <input type="number" step="1" min="0" max="100" value={Math.round(selectedClip.x ?? 50)} onChange={(e) => handleUpdate({ x: parseFloat(e.target.value) || 0 })} className={inputCls} />
                        </Field>
                        <Field label="Y (%)">
                            <input type="number" step="1" min="0" max="100" value={Math.round(selectedClip.y ?? 50)} onChange={(e) => handleUpdate({ y: parseFloat(e.target.value) || 0 })} className={inputCls} />
                        </Field>
                    </div>
                    {(selectedClip.type === 'logo' || selectedClip.type === 'image') && (
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Width (%)">
                                <input type="number" step="1" min="1" max="100" value={Math.round(selectedClip.width ?? 30)} onChange={(e) => handleUpdate({ width: parseFloat(e.target.value) || 10 })} className={inputCls} />
                            </Field>
                            <Field label="Height (%)">
                                <input type="number" step="1" min="1" max="100" value={Math.round(selectedClip.height ?? 30)} onChange={(e) => handleUpdate({ height: parseFloat(e.target.value) || 10 })} className={inputCls} />
                            </Field>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Rotation">
                            <input type="number" step="1" min="-360" max="360" value={Math.round(selectedClip.rotation ?? 0)} onChange={(e) => handleUpdate({ rotation: parseFloat(e.target.value) || 0 })} className={inputCls} />
                        </Field>
                        <Field label="Opacity">
                            <input type="range" min="0" max="1" step="0.05" value={selectedClip.opacity ?? 1} onChange={(e) => handleUpdate({ opacity: parseFloat(e.target.value) })} className="w-full mt-2 accent-violet-500" />
                        </Field>
                    </div>
                    <div className="text-right">
                        <span className="text-[10px] text-zinc-500">{Math.round((selectedClip.opacity ?? 1) * 100)}% opacity</span>
                    </div>
                </div>
            )}

            {/* Text Properties */}
            {selectedClip.type === 'text' && (() => {
                // ── Estimate visual lines so we can warn at >3 ──
                // Frame width @ 92% × char width (≈ 0.55 × fontSize for sans).
                // Aspect ratio drives the canvas width assumption: 16:9 → wide,
                // 9:16 → narrow, 1:1 → square. We use rough render-time pixel
                // widths so the heuristic stays consistent with what the user
                // sees in PreviewMonitor.
                const aspect = project?.aspectRatio || '16:9';
                const canvasPxByAspect = { '16:9': 960, '1:1': 600, '9:16': 360, '4:3': 800, '21:9': 1120 };
                const canvasWidth = canvasPxByAspect[aspect] || 960;
                const usableWidth = canvasWidth * 0.92;
                const fontSize = selectedClip.fontSize || 48;
                const avgCharWidth = fontSize * 0.55;
                const maxCharsPerLine = Math.max(8, Math.floor(usableWidth / avgCharWidth));
                const explicitLines = (selectedClip.content || '').split('\n');
                const visualLines = explicitLines.reduce((acc, line) => {
                    if (line.length === 0) return acc + 1;
                    return acc + Math.ceil(line.length / maxCharsPerLine);
                }, 0);
                const overflow = visualLines > 3;
                return (
                <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Text</p>
                    <Field label="Content">
                        <textarea
                            value={selectedClip.content || ''}
                            onChange={(e) => handleUpdate({ content: e.target.value })}
                            rows={3}
                            placeholder={'Enter text. Press Enter for a new line.\nMax 3 lines.'}
                            className={`${inputCls} resize-y`}
                        />
                    </Field>
                    <div className="text-[10px] text-zinc-500">
                        {visualLines} of 3 lines · ~{maxCharsPerLine} chars/line at {fontSize}px on {aspect}
                    </div>
                    {overflow && (
                        <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-2 py-1.5">
                            Text overflows 3 lines. Reduce characters or lower the font size.
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Font Size">
                            <input type="number" value={selectedClip.fontSize || 48} onChange={(e) => handleUpdate({ fontSize: parseInt(e.target.value) || 48 })} className={inputCls} />
                        </Field>
                        <Field label="Color">
                            <input type="color" value={selectedClip.color || '#ffffff'} onChange={(e) => handleUpdate({ color: e.target.value })} className="w-full h-9 bg-white/5 border border-white/10 rounded-lg cursor-pointer" />
                        </Field>
                    </div>
                    <Field label="Font">
                        <select value={selectedClip.fontFamily || 'sans-serif'} onChange={(e) => handleUpdate({ fontFamily: e.target.value })} className={selectCls}>
                            {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </Field>
                </div>
                );
            })()}

            {/* Audio Properties */}
            {selectedClip.type === 'audio' && (
                <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Audio</p>
                    <Field label="Volume">
                        <input type="range" min="0" max="1" step="0.01" value={selectedClip.volume ?? 1} onChange={(e) => handleUpdate({ volume: parseFloat(e.target.value) })} className="w-full accent-violet-500" />
                    </Field>
                    <span className="text-[10px] text-zinc-500">{Math.round((selectedClip.volume ?? 1) * 100)}%</span>
                </div>
            )}

            {/* Video Properties */}
            {selectedClip.type === 'video' && (
                <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Video</p>
                    <Field label="Speed">
                        <select value={selectedClip.speed || 1} onChange={(e) => handleUpdate({ speed: parseFloat(e.target.value) })} className={selectCls}>
                            <option value={0.5}>0.5x (Slow)</option>
                            <option value={1}>1.0x (Normal)</option>
                            <option value={1.5}>1.5x</option>
                            <option value={2}>2.0x (Fast)</option>
                        </select>
                    </Field>
                    <Field label="Transition">
                        <select value={selectedClip.transition?.type || 'none'} onChange={(e) => handleUpdate({ transition: { type: e.target.value, duration: selectedClip.transition?.duration || 0.5 } })} className={selectCls}>
                            {TRANSITION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    {selectedClip.transition?.type !== 'none' && (
                        <Field label="Transition Duration (s)">
                            <input type="number" step="0.1" value={selectedClip.transition?.duration || 0.5} onChange={(e) => handleUpdate({ transition: { ...selectedClip.transition, duration: parseFloat(e.target.value) || 0.5 } })} className={inputCls} />
                        </Field>
                    )}
                </div>
            )}

            {/* Logo/Image source info */}
            {(selectedClip.type === 'logo' || selectedClip.type === 'image') && selectedClip.name && (
                <div className="pt-2 border-t border-white/10">
                    <p className="text-[10px] text-zinc-600 truncate">File: {selectedClip.name}</p>
                </div>
            )}

            {/* Delete */}
            <button
                onClick={() => onDeleteClip(selectedClip.id)}
                className="w-full mt-auto bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 font-semibold py-2 px-4 rounded-lg flex items-center justify-center transition-colors text-xs"
            >
                <TrashIcon className="w-4 h-4 mr-2" />
                Delete Clip
            </button>
        </div>
    );
};
